/** Minimal toast notifications: `toast.success('Saved')` from anywhere. */

import { useSyncExternalStore } from 'react'

import { cn } from '@/lib/utils'

type Kind = 'success' | 'error' | 'info'
interface Item {
  id: number
  kind: Kind
  message: string
}

let items: Item[] = []
let nextId = 1
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

function push(kind: Kind, message: string) {
  const id = nextId++
  items = [...items, { id, kind, message }]
  emit()
  window.setTimeout(() => {
    items = items.filter((i) => i.id !== id)
    emit()
  }, kind === 'error' ? 7000 : 4000)
}

export const toast = {
  success: (message: string) => push('success', message),
  error: (message: string) => push('error', message),
  info: (message: string) => push('info', message),
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong.'
}

export function Toaster() {
  const current = useSyncExternalStore(
    (l) => {
      listeners.add(l)
      return () => listeners.delete(l)
    },
    () => items,
  )
  return (
    <div className="pointer-events-none fixed bottom-5 right-5 z-[60] flex w-[360px] max-w-[calc(100vw-2.5rem)] flex-col gap-2">
      {current.map((item) => (
        <div
          key={item.id}
          role="status"
          className={cn(
            'pointer-events-auto rounded-field border px-4 py-3 text-[13.5px] shadow-float animate-fade-up',
            item.kind === 'success' && 'border-success/30 bg-surface text-ink',
            item.kind === 'error' && 'border-danger/30 bg-surface text-danger',
            item.kind === 'info' && 'border-line bg-surface text-ink',
          )}
        >
          <span className="mr-2">{item.kind === 'success' ? '✓' : item.kind === 'error' ? '!' : '•'}</span>
          {item.message}
        </div>
      ))}
    </div>
  )
}
